import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { AdminSidebar } from "./AdminSidebar";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";

const gradientBackground =
  "bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.08),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.15),_transparent_55%)]";

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // Manager sur /admin : rediriger vers Utilisateurs (ils n'ont pas accès au dashboard)
    if (
      user &&
      (user.role === "manager_contact" || user.role === "manager_trader") &&
      location.pathname === "/admin"
    ) {
      navigate("/admin/users", { replace: true });
    }
    if (user?.role === "superadmin" && location.pathname === "/admin") {
      navigate("/admin/superadmin/client-investments", { replace: true });
    }
    // Fermer le sidebar mobile lors du changement de route
    setIsSidebarOpen(false);
  }, [location.pathname, user, navigate]);

  useEffect(() => {
    if (!isSidebarOpen) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    // Lock background scroll while the mobile sidebar is open.
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isSidebarOpen]);

  return (
    <div
      className={`min-h-screen bg-background text-foreground transition-colors dark:bg-slate-950/95 dark:text-white ${gradientBackground}`}>
      <div className="flex w-full flex-col gap-6 px-4 py-6 lg:flex-row lg:py-12">
        <AdminSidebar user={user ?? null} className="hidden lg:flex" />
        <Button
          variant="ghost"
          className="flex items-center gap-2 self-start rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 shadow-sm hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:shadow-none dark:hover:bg-white/10 lg:hidden"
          onClick={() => setIsSidebarOpen(true)}>
          <Menu className="h-4 w-4" />
          {t("admin.layoutMenuButton")}
        </Button>

        <div className="flex-1 space-y-6">
          <header className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white px-6 py-4 shadow-xl dark:border-white/10 dark:bg-white/5 dark:shadow-2xl sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-cyan-600 dark:text-cyan-300">
                {t("admin.layoutHeaderPrefix")}
              </p>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                {t("admin.layoutHeaderTitle", {
                  name: user?.firstName ?? "Admin",
                })}
              </h1>
              <p className="text-sm text-slate-600 dark:text-white/70">
                {t("admin.layoutHeaderSubtitle")}
              </p>
            </div>
            <Button
              variant="ghost"
              className="justify-start border border-slate-200 bg-slate-50 px-4 text-slate-800 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              onClick={() => {
                void logout();
              }}>
              <LogOut className="mr-2 h-4 w-4" />
              {t("admin.layoutLogout")}
            </Button>
          </header>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl backdrop-blur dark:border-white/10 dark:bg-white/5 dark:shadow-2xl">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm transition-opacity lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-[101] w-72 overflow-y-auto overscroll-contain bg-slate-100 p-5 shadow-2xl dark:bg-slate-950/95 lg:hidden">
            <div className="mb-4 flex items-center justify-between text-slate-900 dark:text-white">
              <span className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-600 dark:text-white/70">
                {t("admin.layoutHeaderPrefix")}
              </span>
              <button
                type="button"
                aria-label="Fermer le menu"
                onClick={() => setIsSidebarOpen(false)}
                className="rounded-full bg-slate-200 p-2 hover:bg-slate-300 dark:bg-white/10 dark:hover:bg-white/20">
                <X className="h-4 w-4" />
              </button>
            </div>
            <AdminSidebar
              user={user ?? null}
              className="flex max-w-full pb-4"
              onNavigate={() => setIsSidebarOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminLayout;
